import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Heart, BookOpen, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_VERSES = [
  { verse: 'See, I am doing a new thing! Now it springs up; do you not perceive it?', ref: 'Isaiah 43:19' },
  { verse: 'The Lord is close to the brokenhearted and saves those who are crushed in spirit.', ref: 'Psalm 34:18' },
  { verse: 'Commit to the Lord whatever you do, and he will establish your plans.', ref: 'Proverbs 16:3' },
];

export default function TestimonyShareInline({ resolvedCount, bureau, onDismiss, onShared }) {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState(
    resolvedCount
      ? `${resolvedCount} item${resolvedCount === 1 ? '' : 's'} cleared from my ${(bureau || '').toString()} report — God is faithful. Keep pressing forward.`
      : 'God opened a door today — one more step toward financial freedom.'
  );
  const [scripture, setScripture] = useState(SUGGESTED_VERSES[0].verse);
  const [scriptureRef, setScriptureRef] = useState(SUGGESTED_VERSES[0].ref);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Add a short message to share');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/testimonies`,
        {
          message: message.trim(),
          scripture: scripture.trim() || null,
          scripture_ref: scriptureRef.trim() || null,
        },
        { withCredentials: true }
      );
      toast.success('Thank you for sharing!', {
        description: 'Your testimony is now on the wall.',
      });
      onShared && onShared();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post testimony');
    } finally {
      setSubmitting(false);
    }
  };

  if (!expanded) {
    return (
      <div
        className="p-4 border border-[#B76E79]/30 bg-gradient-to-br from-[#E0BFB8]/20 to-white rounded-sm flex items-center justify-between gap-3 animate-fade-in-up"
        data-testid="testimony-share-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#B76E79]/20 flex items-center justify-center flex-shrink-0">
            <Heart className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">
              Share your testimony?
            </p>
            <p className="text-xs text-[#36454F]/65 font-['Manrope'] mt-0.5">
              Your story — anonymous if you want — encourages someone else on the journey.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="sm"
            onClick={() => setExpanded(true)}
            className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"
            data-testid="testimony-share-open-btn"
          >
            Share
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            className="text-[#36454F]/40 hover:text-[#36454F]/70 font-['Manrope'] text-xs"
            data-testid="testimony-share-skip-btn"
          >
            Skip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-4 border border-[#B76E79]/30 bg-gradient-to-br from-[#E0BFB8]/15 to-white rounded-sm space-y-3 animate-fade-in-up"
      data-testid="testimony-share-form"
    >
      <div className="flex items-center justify-between">
        <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm flex items-center gap-2">
          <Heart className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
          Your testimony
        </p>
        <button
          onClick={() => setExpanded(false)}
          className="text-[#36454F]/40 hover:text-[#36454F]/70"
          data-testid="testimony-share-collapse"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <div>
        <label className="text-xs font-['Manrope'] text-[#36454F]/70 block mb-1">Message</label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm min-h-[70px]"
          placeholder="Tell others what God has done…"
          data-testid="testimony-message-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2">
          <label className="text-xs font-['Manrope'] text-[#36454F]/70 block mb-1 flex items-center gap-1">
            <BookOpen className="w-3 h-3" strokeWidth={1.5} /> Scripture (optional)
          </label>
          <Input
            value={scripture}
            onChange={(e) => setScripture(e.target.value)}
            className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm"
            placeholder="Verse text"
            data-testid="testimony-scripture-input"
          />
        </div>
        <div>
          <label className="text-xs font-['Manrope'] text-[#36454F]/70 block mb-1">Reference</label>
          <Input
            value={scriptureRef}
            onChange={(e) => setScriptureRef(e.target.value)}
            className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm"
            placeholder="Isaiah 43:19"
            data-testid="testimony-scripture-ref-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 pt-1">
        {SUGGESTED_VERSES.map((v, i) => (
          <button
            key={i}
            type="button"
            onClick={() => {
              setScripture(v.verse);
              setScriptureRef(v.ref);
            }}
            className="text-[10px] px-2 py-1 rounded-sm border border-[#36454F]/15 text-[#36454F]/70 hover:border-[#B76E79] hover:text-[#B76E79] font-['Manrope']"
            data-testid={`testimony-suggest-verse-${i}`}
          >
            {v.ref}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          onClick={onDismiss}
          className="text-[#36454F]/60 font-['Manrope'] text-xs"
          data-testid="testimony-cancel-btn"
        >
          Maybe later
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !message.trim()}
          className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"
          data-testid="testimony-submit-btn"
        >
          {submitting ? 'Posting…' : 'Post to Wall'}
        </Button>
      </div>
    </div>
  );
}
